import React, { useState, useEffect, useCallback } from 'react';
import { FiLock, FiCheckCircle, FiX } from 'react-icons/fi';
import { isPublicStandalonePath } from '../utils/publicStandaloneRoutes';
import { canContinuePreparedOffline } from '../offline/offlineSession';

export default function SessionExpiredModal() {
    const [visible, setVisible] = useState(false);
    const [sessionRestored, setSessionRestored] = useState(false);
    const [failureReason, setFailureReason] = useState('expired');

    const dismiss = useCallback(() => {
        setVisible(false);
        setSessionRestored(false);
    }, []);

    useEffect(() => {
        const onSessionExpired = (event) => {
            if (canContinuePreparedOffline()) return;
            if (isPublicStandalonePath() || window.location.pathname.startsWith('/admin')) return;
            setSessionRestored(false);
            setFailureReason(event.detail?.reason || 'expired');
            setVisible(true);
        };
        window.addEventListener('vela:session-expired', onSessionExpired);
        return () => window.removeEventListener('vela:session-expired', onSessionExpired);
    }, []);

    useEffect(() => {
        if (!visible) return;

        const onStorage = (e) => {
            if (e.key === 'velaToken' && e.newValue) {
                setSessionRestored(true);
            }
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, [visible]);

    if (!visible) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
                <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                        {sessionRestored ? (
                            <FiCheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                            <FiLock className="w-5 h-5 text-amber-500" />
                        )}
                        <h2 className="text-lg font-semibold text-gray-900">
                            {sessionRestored
                                ? 'Session Restored'
                                : failureReason === 'expired'
                                    ? 'Session Expired'
                                    : 'Sign-in Required'}
                        </h2>
                    </div>
                    <button
                        onClick={dismiss}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        aria-label="Dismiss"
                    >
                        <FiX className="w-5 h-5" />
                    </button>
                </div>

                <div className="px-6 py-5">
                    {sessionRestored ? (
                        <p className="text-gray-600">
                            You've successfully logged in. Your unsaved changes are still here — please save your work now.
                        </p>
                    ) : (
                        <p className="text-gray-600">
                            {failureReason === 'expired'
                                ? 'Your session has expired.'
                                : 'Your sign-in could not be verified.'}{' '}
                            Log in to continue — your unsaved changes will remain on the page while you do.
                        </p>
                    )}
                </div>

                <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
                    {sessionRestored ? (
                        <button
                            onClick={dismiss}
                            className="px-4 py-2 text-sm font-medium text-white bg-[#4f7a64] hover:bg-[#3d6350] rounded-lg transition-colors"
                        >
                            Dismiss and save my work
                        </button>
                    ) : (
                        <>
                            <button
                                onClick={dismiss}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                            >
                                Dismiss
                            </button>
                            <button
                                onClick={() => {
                                    const from = encodeURIComponent(window.location.pathname + window.location.search);
                                    window.open(`/login?from=${from}`, '_blank', 'noopener,noreferrer');
                                }}
                                className="px-4 py-2 text-sm font-medium text-white bg-[#4f7a64] hover:bg-[#3d6350] rounded-lg transition-colors"
                            >
                                Log in
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
